import AssignmentIcon from '@mui/icons-material/Assignment';
import { Chip, TextField, Typography } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import React, { useContext, useEffect, useRef, useState } from "react";
import api from "../../Services/api";
import { errorManagement } from "../../Services/errorManagement";
import FileUploader from "../General/FileUploader";
import { HomeAppContext } from "../Home";
import DataMappingEditor from "./components/DataMappingEditor";

export const DatasetFormMode = {
  NEW: "new",
  EDIT: "edit"
};

export default function Form({
  mode,
  open = false,
  entity = null,
  variables = [],
  setSaveDisabled,
  setEntity,
}) {
  const { homeDispatch } = useContext(HomeAppContext);
  const [isCSVRead, setIsCSVRead] = useState(false);
  const isCSVBeingRead = useRef(false);
  const [csvValues, setCsvValues] = useState([]);
  const [csvVariables, setCsvVariables] = useState([]);
  const [mappingTypeError, setMappingTypeError] = useState("");

    useEffect(() => {
    if (entity && Array.isArray(entity.dataMappings)) {
      const freq = {};
      let hasDuplicate = false;

      for (const m of entity.dataMappings) {
        if (m.type != null) {
          freq[m.type] = (freq[m.type] || 0) + 1;
          if (freq[m.type] > 1) {
            hasDuplicate = true;
            break;
          }
        }
      }

      if (hasDuplicate) {
        setMappingTypeError("Cannot use the same Mapping Type more than once. Please remove duplicates.");
        setSaveDisabled(true);
      } else {
        setMappingTypeError("");
        setSaveDisabled(false);
      }
    } else {
      setMappingTypeError("");
      setSaveDisabled(false);
    }
  }, [entity?.dataMappings]);

  const handleDataMappingsChange = (newMappings) => {
    setEntity(prev => ({
      ...prev,
      dataMappings: newMappings
    }));
  };

  const onInputChange = (event, property) =>
    setEntity({ ...entity, [property]: event.target.value });

  const onValuesInputChange = (event, property) => {
    if (event.target.value !== "") {
      prepareValuesAndVariables(event.target.value);
    } else {
      setEntity((prevEntity) => ({
        ...prevEntity,
        variables: [],
        values: []
      }));
    }
  };

  const prepareValuesAndVariables = (content) => {
    if (content.includes("\t")) {
      const csvConverted = content.replace(/\t/g, ";");
      const valueLines = csvConverted.split("\n");
      const variables = valueLines[0].split(";");
      valueLines.shift();
      setEntity((prevEntity) => ({
        ...prevEntity,
        variables: variables
      }));
      if (valueLines[valueLines.length - 1] === "") {
        valueLines.pop();
      }
      setEntity((prevEntity) => ({
        ...prevEntity,
        values: valueLines
      }));
    }
  };

  const onVariableInputChange = (value, property) => {
    setEntity(prevEntity => {
      const updatedEntity = {
        ...prevEntity,
        [property]: value
      };
      
      if (property === 'variables' && prevEntity.dataMappings && prevEntity.dataMappings.length > 0) {
        const updatedMappings = prevEntity.dataMappings.filter(
          mapping => value.includes(mapping.variableName)
        );
        
        if (updatedMappings.length !== prevEntity.dataMappings.length) {
          updatedEntity.dataMappings = updatedMappings;
        }
      }
      
      return updatedEntity;
    });
  };

  useEffect(() => {
    if (isCSVRead) {
      setEntity((prevEntity) => ({
        ...prevEntity,
        variables: csvVariables,
        values: csvValues
      }));
    }
  }, [isCSVRead, setEntity, csvVariables, csvValues]);

  const onFileUploaded = (value) => {
    if (value.length > 0 && open) {
      if (
        value[0].uploading === false &&
        value[0].progress === 100 &&
        !isCSVRead &&
        !isCSVBeingRead.current
      ) {
        isCSVBeingRead.current = true;
        homeDispatch({
          type: "NOTIFICATION",
          data: { message: "Reading from CSV", loading: true }
        });
        api
          .get("/api/datasets/csv-content/" + value[0].id)
          .then((r) => {
            homeDispatch({
              type: "NOTIFICATION",
              data: { message: "CSV read successfully", type: "success" }
            });
            setCsvVariables(r.data.headers);
            setCsvValues(r.data.rows);
            setIsCSVRead(true);
          })
          .catch((error) => errorManagement.formErrors(error, homeDispatch))
          .finally(() => {
            isCSVBeingRead.current = false;
          });
      }
    } else {
      setEntity((prevEntity) => ({
        ...prevEntity,
        fileIds: [],
        variables: [],
        values: []
      }));
      setIsCSVRead(false);
    }
    setEntity({ ...entity, fileIds: value });
  };

  const shouldShowMappingEditor = entity && entity.variables && entity.variables.length > 0;

  const renderMappingChip = (variable, getTagProps, index) => {
    const isMapped = entity.dataMappings && 
      entity.dataMappings.some(mapping => mapping.variableName === variable);
    
    return (
      <Chip
        {...getTagProps({ index })}
        label={variable}
        variant={isMapped ? "filled" : "outlined"}
        icon={isMapped ? <AssignmentIcon fontSize="small" /> : null}
        style={isMapped ? { backgroundColor: '#4caf50', color: 'white' } : {}}
      />
    );
  };

  return entity === null ? null : (
    <React.Fragment>
      <Grid container spacing={2} justifyContent={"space-between"}>
        <Grid item sm={12}>
          <Grid item sm={5}>
            <TextField
              required
              label="Dataset Name"
              fullWidth
              value={entity.name || ""}
              onChange={(event) => onInputChange(event, "name")}
            />
          </Grid>
        </Grid>

        <Grid item sm={12}>
          <Grid item sm={12}>
            <InputLabel>Import Dataset from CSV file</InputLabel>
          </Grid>
        </Grid>
        <Grid item sm={12}>
          <Grid item sm={12}>
            <FileUploader
              onFileUploaded={onFileUploaded}
              uploadUrl="/api/files"
              acceptedFileExtensions=".csv"
              acceptedFileExtensionsMessage=".csv files only"
              maxFiles={1}
              existingFiles={entity.fileIds && entity.fileIds.length > 0 ? entity.fileIds : []}
            ></FileUploader>
          </Grid>
        </Grid>

        <Grid item sm={12}>
          <Grid item sm={12}>
            <Autocomplete
              required
              freeSolo={true}
              multiple={true}
              disableClearable
              value={entity.variables || []}
              onChange={(event, value) =>
                onVariableInputChange(value, "variables")
              }
              options={variables.map((variable) => variable.text || "")}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => renderMappingChip(option, getTagProps, index))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Dataset Variables"
                  InputProps={{
                    ...params.InputProps,
                    type: "search"
                  }}
                />
              )}
            />
          </Grid>
        </Grid>

        {shouldShowMappingEditor && (
          <Grid item sm={12}>
            <Typography variant="h6">Variable Mappings</Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Assign specific roles to your dataset variables
            </Typography>
            
            <DataMappingEditor
              datasetVariables={entity.variables || []}
              initialMappings={entity.dataMappings || []}
              onChange={handleDataMappingsChange}
              mode={mode}
            />
            {mappingTypeError && (
              <Typography color="error" sx={{ mt: 1 }}>
                {mappingTypeError}
              </Typography>
            )}
          </Grid>
        )}

        <Grid item sm={12}>
          <Grid item sm={12}>
            <TextField
              label="Dataset Values"
              fullWidth
              value={(entity.values || []).join("\n")}
              multiline
              rows={8}
              onChange={(event) => onValuesInputChange(event, "values")}
            />
          </Grid>
        </Grid>

        <Grid item sm={12}>
          <Grid item sm={12}>
            <TextField
              label="Dataset Description"
              fullWidth
              value={entity.description || ""}
              multiline
              rows={4}
              onChange={(event) => onInputChange(event, "description")}
            />
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}
